/// <reference path="../globals.d.ts" />

import moment from 'moment'
import { validateAtLeastOneDigit, validateAtLeastOneLetter, validateMinimumLength } from '@src/util/validation'
import VueAnalytics, { page } from 'vue-analytics'
import Vue from '@src/vue'
import camelcaseKeys from 'camelcase-keys'

import { convertDateTo, convertToYYYYMMDD } from '@src/util/date-format'
import { TrainingDay } from '@src/data/training-plan'
import { WorkoutDataState } from '@src/data/workout-data'
import { WorkoutService } from '@src/service/workout'
import { ExerciseResponse } from '@src/data/exercise-response'
import { WorkoutExercise } from '@src/data/workout-exercise'
import { HealthLimitationSet } from '@src/data/health-limitation-set'
import { APEX_CHARTS_TYPES, SCORE_TYPES } from '@src/util/constants'

// formats a date from one format to another
export function formatDate (date: string, from: string,to: string) {
  let date1 = new Date(moment(date,from).locale('en').format(to))
  return date1
}

// count the occurrence of an id in a list of object
export function countItemAppearance (list: any, itemId: number) {
  let counter = 0
  list.forEach(item => {
    if (item.id === itemId || (item.exercise && item.exercise.id === itemId)) {
      counter++
    }
  })
  return counter
}

// Convert an object to snake case keys
export function snakecaseKeys (object) {
  let convertedObject: any = {}

  // if string, convert it
  if (typeof object === 'string') {
    return stringToSnakecaseKeys(object)
  }

  // if object, convert the fields
  Object.keys(object).forEach(key => {
    convertedObject[stringToSnakecaseKeys(key)] = object[key]
  })

  return convertedObject
}

function stringToSnakecaseKeys (value) {
  let newValue = ''
  let parts: string[] = value.split(/(?=[A-Z,0-9])/)
  parts.forEach(part => {
    if (newValue === '') {
      newValue += part.toLowerCase()
    } else {
      newValue += '_' + part.toLowerCase()
    }
  })
  return newValue
}

export function getDaysNumberBeforeToday (date) {
  if (date === '') {
    return 0
  }
  let now = moment(new Date()) // todays date
  let end = moment(date) // another date
  let duration = moment.duration(now.diff(end))
  return duration.asDays()
}

// Generate all dates (YYYY-MM-DD) between start and end
export function generateAllDays (startDate: Date, endDate: Date) {
  let periods: any[] = []
  let currentDate = startDate
  while (currentDate <= endDate) {
    let formattedDate = convertToYYYYMMDD(currentDate)
    periods.push({ from: formattedDate, to: formattedDate })
    currentDate.setDate(currentDate.getDate() + 1)
  }
  return periods
}

// Generate all months in a year; format: 'YYYY-MM-DD'
export function generateAllMonths (startDate: Date) {
  let year = startDate.getFullYear()
  return moment.months().map((month, index) => {
    return {
      from: convertToYYYYMMDD(new Date(year, index, 1)),
      to: convertToYYYYMMDD(new Date(year, index + 1, 0))
    }
  })
}

// Generate all days in the current week (starting on Monday)
export function getCurrentWeekPeriods () {
  let start = getMonday(new Date())
  let end = new Date(start)
  end.setDate(end.getDate() + 6)
  return generateAllDays(start, end)
}

// Generate all days in the current month (starting on the 1st)
export function getCurrentMonthPeriods () {
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), 1)
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  return generateAllDays(start, end)
}

function getMonday (d) {
  d = new Date(d)
  let day = d.getDay()
  // adjust when day is Sunday
  let diff = d.getDate() - day + (day === 0 ? -6 : 1)

  return new Date(d.setDate(diff))
}

export function compare (a, b, order): number {
  if (order === 'asc') {
    if (a < b) {
      return -1
    }
    if (a > b) {
      return 1
    }
  } else {
    if (b < a) {
      return -1
    }
    if (b > a) {
      return 1
    }
  }

  // names must be equal
  return 0
}

// Will return the difference in days between 2 dates (regardless of the order; will always return a positive number of days)
export function getDaysDifference (date1: Date, date2: Date): number {
  const diff = date1.getTime() - date2.getTime()
  return Math.floor(Math.abs(diff / (1000 * 60 * 60 * 24)))
}

export function isMobileBrowser () {
  return /Android|webOS|iPhone|iPad|BlackBerry|Windows Phone|Opera Mini|IEMobile|Mobile/i.test(navigator.userAgent)
}

export function highlightFulfilledRequirements (newPassword, confirmPassword) {
  return {
    minLength: validateMinimumLength(newPassword, 6),
    oneLetter: validateAtLeastOneLetter(newPassword),
    oneNumber: validateAtLeastOneDigit(newPassword),
    sameAsConfirm: confirmPassword ? newPassword === confirmPassword : false
  }
}

export function stopPropagation (e) {
  e.preventDefault()
  e.stopPropagation()
}

export function track (path) {
  page(path)
}

const TRAINER_UA = 'UA-61841836-9'
const MEMBER_UA = 'UA-61841836-10'

export function setupTrackingID (isTrainer: boolean) {
  let isProduction = NODE_ENV === 'production'
  Vue.use(VueAnalytics,
    {
      id: isTrainer ? TRAINER_UA : MEMBER_UA,
      debug: {
        enabled: false,
        // turn off during development
        sendHitTask: isProduction
      }
    })
}

export function displayDateForChart (point, dateType, splitOnTwoLines = false) {
  let convertedDate = splitOnTwoLines ?
    convertDateTo(new Date(Number(point.value)), dateType === 'month' ? 'MMM,YYYY' : 'D MMM,YYYY')
    : convertDateTo(new Date(Number(point.value)), dateType === 'month' ? 'MMM YYYY' : 'D MMM YYYY')

  return splitOnTwoLines ? convertedDate.split(',') : convertedDate
}

export function getKeysFromJSON (parentKey): string[] {
  let keys: string[] = []
  for (const prop in parentKey) {
    keys.push(prop)
  }
  return keys
}

export function getExerciseSteps (translations, exNumber: number, type: string, stepList: string[]) {
  let counter = 1
  while (counter > 0) {
    const result = translations.$te(`EXERCISE[${exNumber}].DESCRIPTION.${type}.${counter}`)
    if (result) {
      stepList.push(counter.toString())
      counter ++
    } else counter = 0
  }

  return stepList
}

export function getExercisePreparationSteps (translations, exNumber: number, type: string, stepList: string[]) {
  let counter = 1
  while (counter > 0) {
    const result = translations.$te(`EXERCISE[${exNumber}].PREPARATION_INSTRUCTIONS.${counter}.${type}`)
    if (result) {
      stepList.push(counter.toString())
      counter ++
    } else counter = 0
  }

  return stepList
}

export function calculateScoreAverage (resultsArray, key): number {
  // calculate pixScore average for all exercises with a workout Id
  let score = resultsArray.reduce((sum, result) => sum + result[key], 0)
  return (score / Object.keys(resultsArray).length) | 0
}

function getSeriesItemConfigByFilter (filter, scores) {
  if (filter.includes('accuracy')) {
    return {
      data: scores.precisionScores,
      color: '#A600FF'
    }
  } else if (filter.includes('tempo')) {
    return {
      data: scores.tempoScores,
      color: '#4FACFE'
    }
  } else if (filter.includes('range_of_motion')) {
    return {
      data: scores.rangeScores,
      color: '#72ECB2'
    }
  } else if (filter.includes('pix_score')) {
    return {
      data: scores.pixScores,
      color: '#FF4851'
    }
  }

  return null
}

export function getChartSeriesForScores (selectedFilters: string[], scores, translation) {
  const series: any[] = []
  SCORE_TYPES.forEach(item => {
    const itemConfiguration = getSeriesItemConfigByFilter(item, scores)
    if (selectedFilters.includes(item) && itemConfiguration) {
      series.push({
        name: translation.$t(`PAGE.MEMBERS.TRAINING.HISTORY.${item.toUpperCase()}`),
        type: itemConfiguration.data === 1 ? APEX_CHARTS_TYPES.SCATTER : undefined,
        ...itemConfiguration
      })
    }
  })

  return series
}

export async function formatTPDays (startDate, tpDays, emptyTrashedWorkouts: boolean = false) {
  let dayDate = startDate ? moment(startDate, 'YYYY-MM-DD').toDate() : null
  let today = new Date()
  today.setHours(23, 59, 59)
  let nextPosition = 1
  let trainingDays: TrainingDay[] = []
  for (let day of tpDays) {
    let trainingDay = new TrainingDay(camelcaseKeys(day, { deep: true }))
    const restDays = trainingDay.restDays
    const workout = trainingDay.workoutAsArray[0]
    if (!workout || (emptyTrashedWorkouts && workout.state === WorkoutDataState.TRASHED && !trainingDay.immutable)) {
      // same as a day emptied in the training plan builder
      trainingDay.id = null
      trainingDay.workout = null
      trainingDay.workoutAsArray = []
      trainingDay.restDays = 0
    } else {
      let workoutExercises = await getWorkoutDetails(workout.id)
      // getWorkoutDetails always returns an array
      workout.workoutExercises = workoutExercises ? workoutExercises : /* istanbul ignore next */ []
    }

    trainingDay.position = nextPosition
    nextPosition++
    trainingDays.push(trainingDay)

    if (restDays === 0) {
      // move to the next day
      if (dayDate) {
        dayDate.setDate(dayDate.getDate() + 1)
      }
    } else {

      // add empty day slots for the rest days
      for (let i = 0; i < restDays; i++) {
        if (dayDate) {
          // move to next day
          dayDate.setDate(dayDate.getDate() + 1)
        }

        const emptyDay = new TrainingDay({
          position: nextPosition,
          restDays: 0,
          workout: null,
          workoutAsArray: [],
          immutable: dayDate ? dayDate <= today : false
        })
        nextPosition++
        trainingDays.push(emptyDay)
      }
    }
  }
  return trainingDays
}

async function getWorkoutDetails (workoutId) {
  let response: any = await new WorkoutService().getWorkout(workoutId)
  return response.workout.workout_exercises!.map((attrs) => {
    let exercise = new ExerciseResponse(camelcaseKeys(attrs.exercise, { deep: true }))
    let workoutExercise = new WorkoutExercise(camelcaseKeys(attrs))
    workoutExercise.setExercise(exercise)
    return workoutExercise
  })
}

export function doesFileExist (urlToFile) {
  const xhr = new XMLHttpRequest()
  xhr.open('HEAD', urlToFile, false)
  xhr.send()

  return xhr.status !== 404
}

export function getGenderName (value) {
  switch (value) {
    case 'm':
      return 'PAGE.MEMBERS.GENDER.MALE'
    case 'f':
      return 'PAGE.MEMBERS.GENDER.FEMALE'
    default:
      return 'PAGE.MEMBERS.GENDER.DIVERSE'
  }
}

export function healthCheck (memberLimitations: HealthLimitationSet, exerciseLimitations: string[]) {
  for (let i = 0; i < exerciseLimitations.length; i++) {
    const camelCaseLimit = exerciseLimitations[i].replace(/_([a-z])/g, function (g) { return g[1].toUpperCase() })
    if (memberLimitations[camelCaseLimit]) return true
  }
  return false
}

export function camelToSnakeCase (text) {
  return text.replace(/(.)([A-Z][a-z]+)/, '$1_$2').replace(/([a-z0-9])([A-Z])/, '$1_$2').toLowerCase()
}

export function objectToQueryParamString (initialObj) {
  const reducer = (obj, parentPrefix = null) => (prev, key) => {
    const val = obj[key]
    key = encodeURIComponent(key)
    const prefix = parentPrefix ? `${parentPrefix}[]` : key

    if (val == null || typeof val === 'function') {
      prev.push(`${prefix}=`)
      return prev
    }

    if (['number', 'boolean', 'string'].includes(typeof val)) {
      prev.push(`${prefix}=${encodeURIComponent(val)}`)
      return prev
    }

    prev.push(Object.keys(val).reduce(reducer(val, prefix), []).join('&'))
    return prev
  }

  return Object.keys(initialObj).reduce(reducer(initialObj), []).join('&')
}

export function capitalizeFirstLetter (value: string) {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export function formatDuration (durationInSeconds: number) {
  const h = Math.floor((durationInSeconds / (60 * 60)) % 24)
  const m = Math.floor((durationInSeconds / 60) % 60)
  const s = Math.floor(durationInSeconds % 60)

  const hh = (h < 10) ? ('0' + h) : h
  const mm = (m < 10) ? ('0' + m) : m
  const ss = (s < 10) ? ('0' + s) : s

  return hh + ':' + mm + ':' + ss
}

export function computeLastActivityFromAlerts (member) {
  if (member.progression) {
    if (member.progression.attendanceAlert60) {
      return 'PAGE.MEMBERS.LIST.NO_MEMBER_TRAINING_2MONTHS'
    }
    if (member.progression.attendanceAlert30) {
      return 'PAGE.MEMBERS.LIST.NO_MEMBER_TRAINING_MONTH'
    }

    if (member.progression.attendanceAlert14) {
      return 'PAGE.MEMBERS.LIST.NO_MEMBER_TRAINING_14DAYS'
    }
  }
  return 'PAGE.MEMBERS.LIST.NO_MEMBER_FIRST_TRAINING'
}
